import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import OrgSetup from "@/components/OrgSetup";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Briefcase, FileText, Mail, MessageSquare, Plus, Loader2, Ruler, Calculator, Link2, FileSpreadsheet, Calendar, Zap, Bot, TrendingUp, Settings, Sparkles, Target, Satellite, Brain, Upload, Shield, Copy, Check, Layers, Building2, Clock, AlertCircle, Phone, DollarSign, Users, BarChart3, TrendingDown, Eye } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { useWorkflows } from "@/hooks/useWorkflows";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/hooks/useAuth";
import { RecentActivity } from "@/components/RecentActivity";
import { StormWatchWidget } from "@/components/dashboard/StormWatchWidget";
import { NextActionCard } from "@/components/dashboard/NextActionCard";
import { SystemHealthWidget } from "@/components/dashboard/SystemHealthWidget";
import { TopLeadsCard } from "@/components/dashboard/TopLeadsCard";
import { DailySidebar } from "@/components/dashboard/DailySidebar";
import { useJobs } from "@/hooks/useJobs";
import { useLeads } from "@/hooks/useLeads";
import { cn } from "@/lib/utils";
import { MaintenanceAgreementDialog } from "@/components/commercial/MaintenanceAgreementDialog";
import { useInsuranceDashboardStats } from "@/hooks/useInsuranceDashboardStats";
import { useCommercialDashboardStats } from "@/hooks/useCommercialDashboardStats";
import { CoreCutDialog } from "@/components/commercial/CoreCutDialog";
import { SafetyPlanDialog } from "@/components/commercial/SafetyPlanDialog";
import { CallMonitorWidget } from "@/components/call-monitor/CallMonitorWidget";
import { useCallMonitorContext } from "@/contexts/CallMonitorContext";
import { MetricCard } from "@/components/dashboard/MetricCard";
import { RevenueChart } from "@/components/dashboard/RevenueChart";
import { PipelineChart } from "@/components/dashboard/PipelineChart";
import { AIInsightsPanel } from "@/components/dashboard/AIInsightsPanel";
import { ActivityFeed } from "@/components/dashboard/ActivityFeed";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileDashboard } from "@/components/mobile/MobileDashboard";

const Dashboard = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const {
    user,
    orgId,
    loading: authLoading
  } = useAuth();
  const {
    jobs,
    loading: jobsLoading
  } = useJobs(orgId);
  const {
    leads,
    loading: leadsLoading
  } = useLeads(orgId);
  const { stats: insuranceStats, loading: insuranceStatsLoading } = useInsuranceDashboardStats(orgId);
  const { stats: commercialStats, loading: commercialStatsLoading } = useCommercialDashboardStats(orgId);
  const [userName, setUserName] = useState<string>("");
  const [activeView, setActiveView] = useState<"retail" | "insurance" | "commercial">("retail");
  const [scopeAnalysis, setScopeAnalysis] = useState<any>(null);
  const [analyzingScope, setAnalyzingScope] = useState(false);
  const [rebuttalDialogOpen, setRebuttalDialogOpen] = useState(false);
  const [selectedRebuttal, setSelectedRebuttal] = useState<any>(null);
  const [copiedRebuttal, setCopiedRebuttal] = useState(false);
  const [maintenanceDialogOpen, setMaintenanceDialogOpen] = useState(false);
  const [coreCutDialogOpen, setCoreCutDialogOpen] = useState(false);
  const [safetyPlanDialogOpen, setSafetyPlanDialogOpen] = useState(false);
  const {
    setContext: setCallContext,
    clearContext: clearCallContext
  } = useCallMonitorContext();

  // Commercial hardcoded data - Property Managers and Bids
  const PROPERTY_MANAGERS = [{
    id: 1,
    name: 'Sarah Jenkins',
    company: 'CBRE',
    activeProperties: 12,
    potentialValue: 1500000,
    email: 'sjenkins@cbre.com',
    phone: '555-0123'
  }, {
    id: 2,
    name: 'Mike Rodriguez',
    company: 'Cushman & Wakefield',
    activeProperties: 8,
    potentialValue: 980000,
    email: 'mrodriguez@cushwake.com',
    phone: '555-0456'
  }, {
    id: 3,
    name: 'Lisa Chen',
    company: 'JLL',
    activeProperties: 15,
    potentialValue: 2100000,
    email: 'lchen@jll.com',
    phone: '555-0789'
  }, {
    id: 4,
    name: 'David Thompson',
    company: 'Colliers',
    activeProperties: 6,
    potentialValue: 750000,
    email: 'dthompson@colliers.com',
    phone: '555-0321'
  }];
  const BID_DEADLINES = [{
    id: 1,
    property: 'Warehouse 4B',
    type: 'Bid Due',
    deadline: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000),
    // 2 days
    urgency: 'medium',
    value: 125000,
    manager: 'Sarah Jenkins'
  }, {
    id: 2,
    property: 'Main St Plaza',
    type: 'Core Cut',
    deadline: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000),
    // Tomorrow
    urgency: 'high',
    value: 45000,
    manager: 'Mike Rodriguez'
  }, {
    id: 3,
    property: 'Tech Center South',
    type: 'Walkthrough',
    deadline: new Date(Date.now() + 6 * 60 * 60 * 1000),
    // 6 hours
    urgency: 'critical',
    value: 280000,
    manager: 'Lisa Chen'
  }, {
    id: 4,
    property: 'Industrial Park A',
    type: 'Bid Due',
    deadline: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
    // 5 days
    urgency: 'low',
    value: 195000,
    manager: 'David Thompson'
  }];
  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'critical':
        return 'red';
      case 'high':
        return 'orange';
      case 'medium':
        return 'yellow';
      case 'low':
        return 'green';
      default:
        return 'gray';
    }
  };
  const getTimeUntilDeadline = (deadline: Date) => {
    const now = new Date();
    const diff = deadline.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    if (hours < 24) return `${hours}h left`;
    return `${days}d left`;
  };

  // Hardcoded professional rebuttals - instant, no API needed
  const COMMON_REBUTTALS = [{
    id: 'ice-water',
    title: 'Ice & Water Shield (Code)',
    icon: Shield,
    color: 'neon-cyan',
    rebuttal: `Per International Residential Code (IRC) R905.2.8.5, Ice and Water Shield is REQUIRED at eaves in areas where there has been a history of ice forming along the eaves causing a backup of water. This property is located in a climate zone where ice damming is prevalent and documented.

The insurance carrier's scope fails to include this MANDATORY code requirement. This is not an upgrade or betterment - it is a code-mandated life safety item required for proper installation per manufacturer specifications and local building codes.

We are requesting immediate approval for Ice & Water Shield installation covering a minimum of 24 inches from the exterior wall line, as required by IRC and local building officials.

Supporting Documentation:
- IRC Section R905.2.8.5
- Local Building Department Requirements
- Manufacturer Installation Guidelines (attached)`
  }, {
    id: 'overhead-profit',
    title: 'Overhead & Profit (O&P)',
    icon: Calculator,
    color: 'neon-green',
    rebuttal: `Per Xactimate guidelines and industry standard practice, Overhead & Profit (O&P) is REQUIRED when three or more trades are involved in the restoration process OR when the project involves coordination and supervision of multiple subcontractors.

This claim clearly meets the threshold for O&P inclusion:
- Trade #1: Roofing contractor
- Trade #2: Siding/exterior contractor  
- Trade #3: Carpentry/framing contractor
- Trade #4: [Additional trade as applicable]

The denial of O&P on this claim is inconsistent with Xactimate's own published guidelines, insurance industry standards, and multiple case law precedents including:
- Cimino v. Allstate (establishing O&P on multi-trade losses)
- Multiple Department of Insurance rulings supporting O&P inclusion

We are requesting immediate approval for 10% Overhead and 10% Profit on all labor and materials, consistent with industry standards and your carrier's own estimating platform guidelines.`
  }, {
    id: 'ridge-vent',
    title: 'Ridge Vent Ventilation',
    icon: TrendingUp,
    color: 'neon-orange',
    rebuttal: `Per IRC Section R806.2, adequate ventilation is REQUIRED for all enclosed attic spaces. The existing roof system includes ridge vent ventilation as part of its engineered ventilation system. Removal of this system without proper replacement would:

1. Violate building code requirements for attic ventilation
2. Void manufacturer warranties on all roofing materials
3. Create conditions conducive to moisture damage, mold growth, and premature roof system failure
4. Result in a non-code-compliant roof installation

The insurance carrier's scope indicates "remove and dispose" of the existing ridge vent but fails to include replacement. This creates a direct code violation and would result in permit rejection by local building officials.

We are requesting immediate approval for replacement of ridge vent ventilation to maintain code compliance and proper roof system function. This is not a betterment - it is maintenance of the existing, required ventilation system.

Alternative: If carrier wishes to pursue a different ventilation strategy, we will require written approval from both the local building department AND the roofing material manufacturer confirming code compliance and warranty preservation.`
  }, {
    id: 'drip-edge',
    title: 'Drip Edge Requirement',
    icon: FileText,
    color: 'neon-purple',
    rebuttal: `Per International Residential Code (IRC) R905.2.8.5, Drip Edge is REQUIRED at eaves and gables for all asphalt shingle roof installations. This is not optional, negotiable, or subject to the adjuster's interpretation - it is a mandatory code requirement.

The insurance carrier's scope omits this required component. Installation of a roof system without code-required drip edge will result in:
1. Automatic permit rejection by local building officials
2. Immediate void of all manufacturer warranties
3. Non-compliant installation subject to teardown and reinstall
4. Potential water intrusion and fascia damage

Furthermore, Xactimate coding guidelines specifically include drip edge as a standard inclusion in roof replacement estimates. The carrier's omission is inconsistent with their own estimating platform standards.

We are requesting immediate approval for drip edge installation at all eaves and gables as required by IRC, manufacturer specifications, and local building codes. The alternative is a non-compliant, non-warrantied roof installation that will be rejected at final inspection.

Reference Documents:
- IRC Section R905.2.8.5
- [Manufacturer] Installation Manual (attached)
- Local Building Permit Requirements (attached)`
  }, {
    id: 'matching-shingles',
    title: 'Matching Shingles (ITEL)',
    icon: Target,
    color: 'neon-blue',
    rebuttal: `The damaged shingles are a discontinued product no longer available from the manufacturer. Per your policy's Matching and Uniformity provision and applicable state regulations, when materials of like kind and quality cannot be obtained, the carrier is obligated to replace enough additional material to achieve a reasonably uniform appearance.

Current Situation:
- Original Product: [Manufacturer/Product Name]
- Status: DISCONTINUED - confirmed by manufacturer (letter attached)
- Damaged Area: [Location/square footage]
- Visible Area Impact: Partial replacement would result in obvious mismatch visible from ground level

The "Similar But Not Matching" approach suggested by the carrier creates:
1. Obvious aesthetic mismatch reducing property value
2. Potential appraisal issues affecting marketability
3. Clear violation of policy matching provisions
4. Inconsistent with ITEL (Insurance To Exact Loss) regulations in this state

Case Law Support:
- [State] follows the "Reasonably Uniform Appearance" standard
- Multiple DOI rulings supporting full-slope replacement when matching is impossible
- Carrier precedent on similar claims with discontinued products

We are requesting immediate approval for replacement of [affected area/slope/entire roof] to achieve a reasonably uniform appearance as required by policy language and state regulations. The alternative approach of mismatched products is unacceptable and inconsistent with your policy obligations.

Attached Documentation:
- Manufacturer discontinuation letter
- Comparable product analysis showing no reasonable match available
- Photos documenting current roof appearance
- State ITEL regulation reference`
  }];
  const handleRebuttalClick = (rebuttal: any) => {
    setSelectedRebuttal(rebuttal);
    setRebuttalDialogOpen(true);
    setCopiedRebuttal(false);
  };
  const handleCopyRebuttal = async () => {
    if (selectedRebuttal) {
      try {
        await navigator.clipboard.writeText(selectedRebuttal.rebuttal);
        setCopiedRebuttal(true);
        toast.success('Rebuttal copied to clipboard!');
        setTimeout(() => setCopiedRebuttal(false), 2000);
      } catch (err) {
        toast.error('Failed to copy to clipboard');
      }
    }
  };
  const [stats, setStats] = useState({
    activeProjects: 0,
    projectsValue: 0,
    pendingProposals: 0,
    proposalsSent: 0,
    monthlyRevenue: 0,
    closeRate: 0,
    activeCampaigns: 0,
    // Retail-specific stats
    closeRateThisWeek: 0,
    closeRateTrend: 'up' as 'up' | 'down',
    avgTicketPrice: 0,
    financingApprovalRate: 0,
    leadsNeedingFollowUp: [] as any[],
    leadsBySource: {
      'Google LSA': 0,
      'Facebook': 0,
      'Canvas': 0,
      'Other': 0
    },
    // Insurance-specific stats (will be replaced by hook data)
    recoverableDepreciation: 0,
    activeSupplements: 0,
    supplementsValue: 0,
    avgClaimIncrease: 0
  });
  const [pipelineStages, setPipelineStages] = useState<Record<string, number>>({});
  const [propertyManagers, setPropertyManagers] = useState<any[]>([]);
  const [bidDeadlines, setBidDeadlines] = useState<any[]>([]);

  // Load workflow for current view (read-only for pipeline display)
  const {
    workflow,
    loading: workflowLoading,
  } = useWorkflows(orgId || "", activeView);
  const statsLoading = jobsLoading || leadsLoading;
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [authLoading, user, navigate]);
  useEffect(() => {
    if (user && orgId) {
      // Load user name
      supabase.from('profiles').select('full_name, email').eq('id', user.id).single().then(({
        data: profile
      }) => {
        if (profile) {
          setUserName(profile.full_name || profile.email?.split('@')[0] || "");
        }
      });
    }
  }, [user, orgId]);

  // Set call monitor context for dashboard
  useEffect(() => {
    setCallContext({
      activeJobId: null,
      activeLeadId: null,
      callMode: 'new-lead'
    });
    return () => {
      clearCallContext();
    };
  }, [setCallContext, clearCallContext]);

  // Update stats when insurance/commercial stats load
  useEffect(() => {
    if (activeView === 'insurance') {
      setStats(prev => ({
        ...prev,
        recoverableDepreciation: insuranceStats.recoverableDepreciation,
        activeSupplements: insuranceStats.activeSupplements,
        supplementsValue: insuranceStats.supplementsValue,
        avgClaimIncrease: insuranceStats.avgClaimIncrease
      }));
    }
  }, [activeView, insuranceStats]);
  const handleScopeUpload = async (file: File) => {
    if (!file) return;
    setAnalyzingScope(true);
    setScopeAnalysis(null);
    try {
      // Read file as text (simplified - in production, use PDF parser)
      const text = await file.text();
      const {
        data,
        error
      } = await supabase.functions.invoke('analyze-adjuster-scope', {
        body: {
          jobId: 'temp',
          documentText: text
        }
      });
      if (error) throw error;
      if (data?.analysis) {
        setScopeAnalysis(data.analysis);
        toast.success('Scope analyzed successfully');
      }
    } catch (error) {
      console.error('Error analyzing scope:', error);
      toast.error('Failed to analyze scope');
    } finally {
      setAnalyzingScope(false);
    }
  };

  // Calculate stats whenever jobs, leads, or workflow changes
  useEffect(() => {
    if (!jobs || !leads) return;

    // Calculate stats
    const activeJobs = jobs.filter(j => ['new', 'contacted', 'scheduled', 'in_progress'].includes(j.status));
    const totalValue = jobs.reduce((sum, j) => sum + (j.estimated_value || 0), 0);

    // Retail-specific calculations
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    const twoWeeksAgo = new Date();
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
    const leadsThisWeek = leads.filter(l => new Date(l.created_at) >= oneWeekAgo);
    const leadsLastWeek = leads.filter(l => new Date(l.created_at) >= twoWeeksAgo && new Date(l.created_at) < oneWeekAgo);
    const closedThisWeek = leadsThisWeek.filter(l => l.status === 'won').length;
    const closedLastWeek = leadsLastWeek.filter(l => l.status === 'won').length;
    const closeRateThisWeek = leadsThisWeek.length > 0 ? closedThisWeek / leadsThisWeek.length * 100 : 0;
    const closeRateLastWeek = leadsLastWeek.length > 0 ? closedLastWeek / leadsLastWeek.length * 100 : 0;
    const closeRateTrend = closeRateThisWeek >= closeRateLastWeek ? 'up' : 'down';
    const avgTicketPrice = jobs.length > 0 ? totalValue / jobs.length : 0;

    // Mock financing approval rate (would come from actual data)
    const financingApprovalRate = 78;

    // Find leads that received a quote but haven't signed
    const leadsNeedingFollowUp = leads.filter(l => ['contacted', 'qualified', 'proposal_sent'].includes(l.status || '') && l.estimated_value && l.estimated_value > 0).slice(0, 5);

    // Calculate lead sources
    const leadsBySource = leads.reduce((acc, lead) => {
      const source = lead.source || 'Other';
      if (source.toLowerCase().includes('google') || source.toLowerCase().includes('lsa')) {
        acc['Google LSA']++;
      } else if (source.toLowerCase().includes('facebook') || source.toLowerCase().includes('fb')) {
        acc['Facebook']++;
      } else if (source.toLowerCase().includes('canvas') || source.toLowerCase().includes('door')) {
        acc['Canvas']++;
      } else {
        acc['Other']++;
      }
      return acc;
    }, {
      'Google LSA': 0,
      'Facebook': 0,
      'Canvas': 0,
      'Other': 0
    });
    setStats({
      activeProjects: activeJobs.length,
      projectsValue: totalValue,
      pendingProposals: jobs.filter(j => j.status === 'scheduled').length,
      proposalsSent: jobs.filter(j => j.status === 'inspected').length,
      monthlyRevenue: totalValue * 0.67,
      closeRate: 67,
      activeCampaigns: leads.filter(l => l.status === 'new').length,
      closeRateThisWeek: Math.round(closeRateThisWeek),
      closeRateTrend,
      avgTicketPrice,
      financingApprovalRate,
      leadsNeedingFollowUp,
      leadsBySource,
      // Insurance stats (mock for now - would calculate from actual supplement/claim data)
      recoverableDepreciation: 145000,
      activeSupplements: 12,
      supplementsValue: 38000,
      avgClaimIncrease: 22
    });

    // Calculate pipeline stats based on workflow stages
    if (workflow && workflow.stages) {
      const stageCounts: Record<string, number> = {};
      workflow.stages.forEach(stage => {
        const jobStatus = stage.job_status;
        const leadStatus = stage.lead_status;
        let count = 0;
        if (jobStatus) {
          count += jobs.filter(j => j.status === jobStatus).length;
        }
        if (leadStatus) {
          count += leads.filter(l => l.status === leadStatus).length;
        }
        stageCounts[stage.stage_key] = count;
      });
      setPipelineStages(stageCounts);
    }
  }, [jobs, leads, workflow]);

  // Commercial portfolio stats - now handled by hook
  useEffect(() => {
    if (!jobs || jobs.length === 0) return;
    const commercialJobs = jobs.filter(j => j.property_type?.toLowerCase() === 'commercial');
    if (commercialJobs.length === 0) {
      setPropertyManagers([]);
      return;
    }

    // Group commercial jobs by property manager/owner
    const managerMap = new Map<string, {
      name: string;
      company: string;
      email: string;
      phone: string;
      properties: number;
      totalValue: number;
    }>();
    commercialJobs.forEach(job => {
      const managerName = job.owner_name || 'Unknown Manager';
      const existing = managerMap.get(managerName);
      if (existing) {
        existing.properties += 1;
        existing.totalValue += job.estimated_value || 0;
      } else {
        managerMap.set(managerName, {
          name: managerName,
          company: job.insurance_company || 'N/A',
          // using insurance_company field as placeholder
          email: job.owner_email || '',
          phone: job.owner_phone || '',
          properties: 1,
          totalValue: job.estimated_value || 0
        });
      }
    });

    // Convert to array and sort by total value
    const managersArray = Array.from(managerMap.values()).sort((a, b) => b.totalValue - a.totalValue).slice(0, 4); // Top 4 managers

    setPropertyManagers(managersArray);
  }, [jobs]);

  // Fetch bid deadlines from calendar events
  useEffect(() => {
    if (!orgId || activeView !== 'commercial') return;
    const fetchBidDeadlines = async () => {
      try {
        const now = new Date();
        const futureDate = new Date();
        futureDate.setDate(futureDate.getDate() + 14); // Next 2 weeks

        const {
          data,
          error
        } = await supabase.from('calendar_events').select('*').eq('org_id', orgId).gte('start_time', now.toISOString()).lte('start_time', futureDate.toISOString()).in('event_type', ['bid_deadline', 'walkthrough', 'core_cut', 'inspection']).order('start_time', {
          ascending: true
        }).limit(4);
        if (error) throw error;
        const formattedDeadlines = (data || []).map(event => {
          const deadline = new Date(event.start_time);
          const hoursUntil = (deadline.getTime() - now.getTime()) / (1000 * 60 * 60);
          let urgency = 'low';
          if (hoursUntil <= 6) urgency = 'critical';else if (hoursUntil <= 24) urgency = 'high';else if (hoursUntil <= 48) urgency = 'medium';
          return {
            id: event.id,
            property: event.title,
            type: event.event_type === 'bid_deadline' ? 'Bid Due' : event.event_type === 'core_cut' ? 'Core Cut' : event.event_type === 'walkthrough' ? 'Walkthrough' : 'Inspection',
            deadline,
            urgency,
            value: 0,
            // Could link to job estimated_value if job_id is present
            manager: event.location || 'N/A'
          };
        });
        setBidDeadlines(formattedDeadlines);
      } catch (error) {
        console.error('Error fetching bid deadlines:', error);
      }
    };
    fetchBidDeadlines();
  }, [orgId, activeView]);
  if (authLoading) {
    return <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>;
  }
  if (user && !orgId) {
    return <OrgSetup />;
  }
  
  // Render mobile dashboard on mobile devices
  if (isMobile) {
    return <MobileDashboard />;
  }
  
  const todayDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const handleStageClick = (status: string) => {
    navigate(`/jobs?status=${status}&view=${activeView}`);
  };
  return <>
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 relative overflow-hidden">
      {/* Glassmorphic decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      </div>
      
      <div className="relative flex gap-6 p-6">
        {/* Main Content */}
        <div className="flex-1 space-y-4 md:space-y-6">
        {/* Welcome Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text">
              Welcome back, {userName}!
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">{todayDate}</p>
          </div>
          <div className="flex gap-3">
            <StormWatchWidget />
            <SystemHealthWidget />
          </div>
        </div>

        {/* Mode Toggles */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex gap-1.5 md:gap-2 overflow-x-auto pb-2 sm:pb-0">
              <Button variant={activeView === "retail" ? "default" : "outline"} onClick={() => setActiveView("retail")} className={cn("whitespace-nowrap backdrop-blur-sm", activeView === "retail" ? "bg-primary/90 hover:bg-primary" : "bg-white/5 hover:bg-white/10 border-white/20")} size="sm">
                Retail
              </Button>
              <Button variant={activeView === "insurance" ? "default" : "outline"} onClick={() => setActiveView("insurance")} className={cn("whitespace-nowrap backdrop-blur-sm", activeView === "insurance" ? "bg-primary/90 hover:bg-primary" : "bg-white/5 hover:bg-white/10 border-white/20")} size="sm">
                Insurance
              </Button>
            <Button variant={activeView === "commercial" ? "default" : "outline"} onClick={() => setActiveView("commercial")} className={cn("whitespace-nowrap", activeView === "commercial" ? "bg-action hover:bg-action-hover" : "")} size="sm">
              Commercial
            </Button>
          </div>
          
          <div className="flex flex-wrap gap-2 items-center">
            <Button variant="outline" onClick={() => navigate("/jobs")} size="sm" className="flex-1 sm:flex-none">
              <Briefcase className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">All Jobs</span>
              <span className="sm:hidden">Jobs</span>
            </Button>
          </div>
        </div>

        {statsLoading ? <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div> : <>
            {/* Stats Cards - Retail Mode */}
            {activeView === "retail" && <div className="flex gap-6">
                {/* Main Content Area - 2/3 width */}
                <div className="flex-1 space-y-6">
                  {/* Hero Metrics Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <MetricCard title="Total Revenue" value={`$${(stats.monthlyRevenue / 1000).toFixed(0)}K`} icon={DollarSign} trend={{
                    value: 12.5,
                    direction: "up"
                  }} sparklineData={[45, 52, 48, 61, 55, 67, 70]} onClick={() => navigate('/jobs')} />
                    <MetricCard title="Close Rate" value={`${stats.closeRateThisWeek}%`} icon={Target} trend={{
                    value: 8.3,
                    direction: "up"
                  }} sparklineData={[35, 40, 38, 45, 42, 48, stats.closeRateThisWeek]} onClick={() => navigate('/jobs?status=proposal_sent')} />
                    <MetricCard title="Active Leads" value={leads.filter(l => l.status === 'new' || l.status === 'contacted').length} icon={Users} trend={{
                    value: 15,
                    direction: "up"
                  }} sparklineData={[12, 15, 14, 18, 19, 21, leads.filter(l => l.status === 'new' || l.status === 'contacted').length]} onClick={() => navigate('/leads')} />
                    <MetricCard title="Avg. Ticket" value={`$${(stats.avgTicketPrice / 1000).toFixed(1)}K`} icon={BarChart3} trend={{
                    value: 5.2,
                    direction: "up"
                  }} sparklineData={[8.2, 9.1, 8.8, 10.2, 9.5, 11.1, stats.avgTicketPrice / 1000]} onClick={() => navigate('/jobs')} />
                  </div>

                  {/* Charts Row */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <RevenueChart />
                    <PipelineChart />
                  </div>

                  {/* Lead Activity & Performance */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <MetricCard title="Response Time" value="2.3 hrs" icon={Clock} trend={{
                    value: 18,
                    direction: "down"
                  }} onClick={() => navigate('/leads')} />
                    <MetricCard title="Proposal Pending" value={stats.pendingProposals} icon={FileText} trend={{
                    value: 3,
                    direction: "neutral"
                  }} onClick={() => navigate('/jobs?status=proposal_sent')} />
                  </div>


                  {/* Pipeline Visualization */}
                  <Card className="backdrop-blur-xl bg-white/10 dark:bg-black/10 border-white/20">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold">Pipeline Stages</h3>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => navigate('/workflow-automation')}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View Workflow
                        </Button>
                      </div>
                      {workflowLoading ? <div className="flex items-center justify-center py-12">
                          <Loader2 className="w-8 h-8 animate-spin text-primary" />
                        </div> : <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                          {workflow?.stages.map(stage => <div key={stage.stage_key} className="group relative cursor-pointer backdrop-blur-sm bg-white/5 hover:bg-white/10 dark:bg-black/5 dark:hover:bg-black/10 border border-white/20 rounded-xl p-4 transition-all duration-300 hover:scale-105" onClick={() => handleStageClick(stage.job_status || stage.stage_key)}>
                              <div className="flex items-center gap-2 mb-3">
                                <div className="w-2 h-2 rounded-full" style={{
                            backgroundColor: stage.color,
                            boxShadow: `0 0 8px ${stage.color}`
                          }} />
                                <p className="text-xs font-semibold uppercase tracking-wide truncate">
                                  {stage.stage_name}
                                </p>
                              </div>
                              <div className="text-4xl font-bold mb-1" style={{
                          color: stage.color
                        }}>
                                {pipelineStages[stage.stage_key] || 0}
                              </div>
                              <p className="text-xs text-muted-foreground">JOBS</p>
                            </div>)}
                        </div>}
                    </CardContent>
                  </Card>
                </div>
              </div>}
          
            {/* Insurance Mode */}
            {activeView === "insurance" && <div className="flex gap-6">
                <div className="flex-1 space-y-6">
                  {/* Insurance Hero Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <MetricCard 
                      title="Recoverable Depreciation" 
                      value={`$${(stats.recoverableDepreciation / 1000).toFixed(0)}K`}
                      icon={DollarSign} 
                      trend={{ value: 18, direction: "up" }}
                      sparklineData={[95, 110, 125, 135, 142, 145, stats.recoverableDepreciation / 1000]}
                      onClick={() => navigate('/jobs?type=insurance')}
                    />
                    <MetricCard 
                      title="Active Supplements" 
                      value={stats.activeSupplements}
                      icon={FileText} 
                      trend={{ value: 12, direction: "up" }}
                      sparklineData={[6, 8, 9, 10, 11, 12, stats.activeSupplements]}
                      onClick={() => navigate('/jobs?status=supplement')}
                    />
                    <MetricCard 
                      title="Supplements Value" 
                      value={`$${(stats.supplementsValue / 1000).toFixed(0)}K`}
                      icon={TrendingUp} 
                      trend={{ value: 22, direction: "up" }}
                      sparklineData={[20, 24, 28, 32, 35, 38, stats.supplementsValue / 1000]}
                      onClick={() => navigate('/jobs')}
                    />
                    <MetricCard 
                      title="Avg. Claim Increase" 
                      value={`${stats.avgClaimIncrease}%`}
                      icon={BarChart3} 
                      trend={{ value: 5, direction: "up" }}
                      sparklineData={[15, 17, 18, 19, 21, 22, stats.avgClaimIncrease]}
                      onClick={() => navigate('/jobs')}
                    />
                  </div>

                  {/* Charts Row */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <RevenueChart />
                    <PipelineChart />
                  </div>


                  {/* Pipeline Visualization */}
                  <Card className="backdrop-blur-xl bg-white/10 dark:bg-black/10 border-white/20">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold">Pipeline Stages</h3>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => navigate('/workflow-automation')}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View Workflow
                        </Button>
                      </div>
                      {workflowLoading ? <div className="flex items-center justify-center py-12">
                          <Loader2 className="w-8 h-8 animate-spin text-primary" />
                        </div> : <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                          {workflow?.stages.map(stage => <div key={stage.stage_key} className="group relative cursor-pointer backdrop-blur-sm bg-white/5 hover:bg-white/10 dark:bg-black/5 dark:hover:bg-black/10 border border-white/20 rounded-xl p-4 transition-all duration-300 hover:scale-105" onClick={() => handleStageClick(stage.job_status || stage.stage_key)}>
                              <div className="flex items-center gap-2 mb-3">
                                <div className="w-2 h-2 rounded-full" style={{
                            backgroundColor: stage.color,
                            boxShadow: `0 0 8px ${stage.color}`
                          }} />
                                <p className="text-xs font-semibold uppercase tracking-wide truncate">
                                  {stage.stage_name}
                                </p>
                              </div>
                              <div className="text-4xl font-bold mb-1" style={{
                          color: stage.color
                        }}>
                                {pipelineStages[stage.stage_key] || 0}
                              </div>
                              <p className="text-xs text-muted-foreground">JOBS</p>
                            </div>)}
                        </div>}
                    </CardContent>
                  </Card>
                </div>
              </div>}

            {/* Commercial Mode */}
            {activeView === "commercial" && <div className="flex gap-6">
                <div className="flex-1 space-y-6">
                  {/* Commercial Hero Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <MetricCard 
                      title="Active Bid Volume" 
                      value={`$${(commercialStats.activeBidVolume / 1000).toFixed(0)}K`}
                      icon={DollarSign} 
                      trend={{ value: 15, direction: "up" }}
                      sparklineData={[180, 210, 240, 280, 320, 350, commercialStats.activeBidVolume / 1000]}
                      onClick={() => navigate('/jobs?type=commercial&status=proposal_sent')}
                    />
                    <MetricCard 
                      title="Recurring Revenue" 
                      value={`$${(commercialStats.recurringRevenue / 1000).toFixed(0)}K/mo`}
                      icon={TrendingUp} 
                      trend={{ value: 8, direction: "up" }}
                      sparklineData={[15, 18, 22, 24, 27, 29, commercialStats.recurringRevenue / 1000]}
                      onClick={() => navigate('/jobs?status=maintenance')}
                    />
                    <MetricCard 
                      title="Win Rate" 
                      value={`${commercialStats.winRate.toFixed(0)}%`}
                      icon={Target} 
                      trend={{ value: 12, direction: "up" }}
                      sparklineData={[35, 38, 42, 45, 48, 52, commercialStats.winRate]}
                      onClick={() => navigate('/jobs?type=commercial')}
                    />
                    <MetricCard 
                      title="Production Backlog" 
                      value={`${commercialStats.productionBacklogSquares} sq`}
                      icon={Layers} 
                      trend={{ value: 22, direction: "up" }}
                      sparklineData={[120, 140, 160, 180, 210, 240, commercialStats.productionBacklogSquares]}
                      onClick={() => navigate('/jobs?status=sold')}
                    />
                  </div>

                  {/* Charts Row */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <RevenueChart />
                    <PipelineChart />
                  </div>


                  {/* Pipeline Visualization */}
                  <Card className="backdrop-blur-xl bg-white/10 dark:bg-black/10 border-white/20">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold">Pipeline Stages</h3>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => navigate('/workflow-automation')}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View Workflow
                        </Button>
                      </div>
                      {workflowLoading ? <div className="flex items-center justify-center py-12">
                          <Loader2 className="w-8 h-8 animate-spin text-primary" />
                        </div> : <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                          {workflow?.stages.map(stage => <div key={stage.stage_key} className="group relative cursor-pointer backdrop-blur-sm bg-white/5 hover:bg-white/10 dark:bg-black/5 dark:hover:bg-black/10 border border-white/20 rounded-xl p-4 transition-all duration-300 hover:scale-105" onClick={() => handleStageClick(stage.job_status || stage.stage_key)}>
                              <div className="flex items-center gap-2 mb-3">
                                <div className="w-2 h-2 rounded-full" style={{
                            backgroundColor: stage.color,
                            boxShadow: `0 0 8px ${stage.color}`
                          }} />
                                <p className="text-xs font-semibold uppercase tracking-wide truncate">
                                  {stage.stage_name}
                                </p>
                              </div>
                              <div className="text-4xl font-bold mb-1" style={{
                          color: stage.color
                        }}>
                                {pipelineStages[stage.stage_key] || 0}
                              </div>
                              <p className="text-xs text-muted-foreground">JOBS</p>
                            </div>)}
                        </div>}
                    </CardContent>
                  </Card>
                </div>
              </div>}
          </>}
      </div>

      {/* Right Sidebar - Daily Overview */}
      <div className="hidden xl:block w-80 space-y-4">
        <DailySidebar />
        <TopLeadsCard leads={stats.leadsNeedingFollowUp.map(lead => ({
            ...lead,
            lastContact: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
          }))} />
        <AIInsightsPanel />
        <RecentActivity />
      </div>
    </div>
    </div>

      {/* Rebuttal Dialog */}
      <Dialog open={rebuttalDialogOpen} onOpenChange={setRebuttalDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-xl">
              {selectedRebuttal && <>
                  <div className={cn("p-2 rounded-lg", `bg-${selectedRebuttal.color}/20`)}>
                    {selectedRebuttal.icon && <selectedRebuttal.icon className={cn("w-5 h-5", `text-${selectedRebuttal.color}`)} />}
                  </div>
                  {selectedRebuttal.title}
                </>}
            </DialogTitle>
          </DialogHeader>
          
          {selectedRebuttal && <div className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-6 font-mono text-sm whitespace-pre-wrap leading-relaxed">
                {selectedRebuttal.rebuttal}
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t">
                <p className="text-xs text-muted-foreground">
                  Ready-to-send professional rebuttal with code references
                </p>
                <Button onClick={handleCopyRebuttal} className="gap-2" variant={copiedRebuttal ? "secondary" : "default"}>
                  {copiedRebuttal ? <>
                      <Check className="w-4 h-4" />
                      Copied!
                    </> : <>
                      <Copy className="w-4 h-4" />
                      Copy to Clipboard
                    </>}
                </Button>
              </div>
              
              <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <Sparkles className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-blue-800 dark:text-blue-200">
                    <strong>Pro Tip:</strong> Customize this template with specific details from your claim 
                    (exact product names, dates, photos, etc.) before sending to maximize impact.
                  </p>
                </div>
              </div>
            </div>}
        </DialogContent>
      </Dialog>
      
      {/* Commercial Action Dialogs */}
      {orgId && <>
          <MaintenanceAgreementDialog open={maintenanceDialogOpen} onOpenChange={setMaintenanceDialogOpen} orgId={orgId} />
          <CoreCutDialog open={coreCutDialogOpen} onOpenChange={setCoreCutDialogOpen} orgId={orgId} />
          <SafetyPlanDialog open={safetyPlanDialogOpen} onOpenChange={setSafetyPlanDialogOpen} orgId={orgId} />
        </>}
    </>;
};
export default Dashboard;